import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { TastingExperienceForm } from '../components/TastingExperienceForm';
import type { TastingExperienceFormData } from '../types/tastingExperience';

export const TastingExperienceCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get('venueId') || undefined;
  
  const [error, setError] = useState<string | null>(null);
  const formSubmitRef = useRef<(() => Promise<void>) | null>(null);

  const createExperience = trpc.tastingExperience.create.useMutation({
    onSuccess: (data) => {
      // A venue admin who started here from the venue management hub is still
      // filling out that venue, so send them back to it. Otherwise continue to
      // the new experience's edit page, where its status and media live.
      if (venueId) {
        navigate(`/wineries/${venueId}/manage`);
      } else {
        navigate(`/tasting-experiences/${data.id}/edit`);
      }
    },
    onError: (error) => {
      setError(error.message);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const handleSubmit = async (data: TastingExperienceFormData) => {
    setError(null);
    await createExperience.mutateAsync(data);
  };

  const handleCreateClick = async () => {
    if (formSubmitRef.current) {
      await formSubmitRef.current();
    }
  };

  const handleCancel = () => {
    // Back to wherever the flow started: the venue's management hub, or the
    // public experiences list for an experience created outside a venue.
    if (venueId) {
      navigate(`/wineries/${venueId}/manage`);
    } else {
      navigate('/tasting-experiences');
    }
  };

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="mt-2">
        <div className="pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Create Tasting Experience</h1>
          
          {/* Action Buttons - Top of Page */}
          <div className="flex gap-4 mb-6 justify-end">
            <Button
              variant="primary"
              onClick={handleCreateClick}
              disabled={createExperience.isPending}
              loading={createExperience.isPending}
            >
              {createExperience.isPending ? 'Creating...' : 'Create Experience'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <svg
                className="w-5 h-5 text-red-400 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Error creating tasting experience
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tasting Experience Form */}
        <div className="mt-6">
          <TastingExperienceForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            venueId={venueId}
            isSubmitting={createExperience.isPending}
            hideButtons
            submitRef={formSubmitRef}
          />
        </div>
      </PageContainer>
    </div>
  );
};
